// Displays a carousel of color swatches

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { cn } from '@/utilities/cn';
import {
  getColorStop,
  type PlayerColorFamily,
} from '../../styles/playerColors';

interface ColorCarouselProps {
  colors: readonly PlayerColorFamily[]; // The array of color keys to choose from
  selectedColor: PlayerColorFamily; // The currently selected color key
  onColorSelect: (color: PlayerColorFamily) => void; // Callback when a color is selected, receives the new color key
  className?: string; // Optional className to apply to container
  trackClassName?: string; // Optional className to tune the track box
  enableKeyboard?: boolean; // Whether to enable keyboard navigation (default: true)
  /** When set, renders this instead of a flat color fill (e.g. Feed The Farm animal Happy SVG). */
  renderSwatch?: (colorKey: PlayerColorFamily) => React.ReactNode;
}

/**
 * Carousel selector by player color key.
 * Default: circular swatches filled with that color’s hex.
 * Optional `renderSwatch` replaces the fill (e.g. animal portrait for Feed The Farm).
 */
const ColorCarousel: React.FC<ColorCarouselProps> = ({
  colors,
  selectedColor,
  onColorSelect,
  className = '',
  trackClassName = '',
  enableKeyboard = true,
  renderSwatch,
}) => {
  // Find the index of the selected color
  const selectedIndex = colors.findIndex((color) => color === selectedColor);
  const [centerIndex, setCenterIndex] = useState(
    selectedIndex !== -1 ? selectedIndex : 0
  );

  // Refs for touch/mouse interaction
  const carouselRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0); // To detect vertical scrolling
  const isScrolling = useRef(false);

  // Handle carousel navigation - memoized with useCallback
  const moveCarousel = useCallback(
    (direction: number) => {
      const totalColors = colors.length;
      const newIndex = (centerIndex + direction + totalColors) % totalColors;
      setCenterIndex(newIndex);
      onColorSelect(colors[newIndex]);
    },
    [centerIndex, colors, onColorSelect]
  );

  // Update centerIndex when selectedColor changes externally
  useEffect(() => {
    const index = colors.findIndex((color) => color === selectedColor);
    if (index !== -1 && index !== centerIndex) {
      setCenterIndex(index);
    } else if (index === -1 && colors.length > 0) {
      // Selected color is no longer available, find the closest available color
      // Stay at the same position if possible, otherwise move to the nearest available
      const newIndex = Math.min(centerIndex, colors.length - 1);
      setCenterIndex(newIndex);
      onColorSelect(colors[newIndex]);
    }
  }, [selectedColor, colors, centerIndex, onColorSelect]);

  // Set up keyboard navigation
  useEffect(() => {
    if (!enableKeyboard) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        moveCarousel(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        moveCarousel(1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enableKeyboard, moveCarousel]); // Now correctly includes moveCarousel

  // Calculate visible colors (show 7 at a time)
  const getVisibleColors = useCallback(() => {
    const totalColors = colors.length;
    const repeatedColors = [...colors, ...colors, ...colors];
    const startIndex = centerIndex + totalColors - 3;
    return repeatedColors.slice(startIndex, startIndex + 7);
  }, [centerIndex, colors]);

  // Mouse/Touch event handlers
  const handleStart = useCallback((clientX: number, clientY: number) => {
    if (!carouselRef.current) return;
    isDragging.current = true;
    isScrolling.current = false;
    startX.current = clientX;
    startY.current = clientY;
  }, []);

  const handleMove = useCallback(
    (clientX: number, clientY: number) => {
      if (!isDragging.current || !carouselRef.current) return;

      // Check if user is trying to scroll vertically
      const deltaY = Math.abs(clientY - startY.current);
      const deltaX = Math.abs(clientX - startX.current);

      // If vertical movement is greater, consider it a scroll
      if (deltaY > deltaX && deltaY > 10) {
        isScrolling.current = true;
        isDragging.current = false;
        return;
      }

      if (isScrolling.current) return;

      const diff = clientX - startX.current;

      // If enough horizontal movement, change the color
      if (Math.abs(diff) > 30) {
        moveCarousel(diff > 0 ? -1 : 1);
        isDragging.current = false;
      }
    },
    [moveCarousel]
  );

  const handleEnd = useCallback(() => {
    isDragging.current = false;
    isScrolling.current = false;
  }, []);

  // Touch event handlers
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      handleStart(e.touches[0].clientX, e.touches[0].clientY);
    },
    [handleStart]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      handleMove(e.touches[0].clientX, e.touches[0].clientY);
    },
    [handleMove]
  );

  const handleTouchEnd = useCallback(() => {
    handleEnd();
  }, [handleEnd]);

  // Mouse event handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      handleStart(e.clientX, e.clientY);
    },
    [handleStart]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      handleMove(e.clientX, e.clientY);
    },
    [handleMove]
  );

  const handleMouseUp = useCallback(() => {
    handleEnd();
  }, [handleEnd]);

  // Get visible colors for the carousel
  const visibleColors = getVisibleColors();

  const getItemClassName = (index: number, isCenter: boolean) =>
    cn(
      'relative flex size-12 shrink-0 cursor-pointer items-center justify-center rounded-full transition-all duration-300',
      isCenter && 'size-14 z-10',
      renderSwatch && 'overflow-hidden bg-neutral-900',
      index === 0 && 'scale-[0.72]',
      index === 1 && 'scale-80',
      index === 2 && 'scale-[0.88]',
      index === 4 && 'scale-[0.88]',
      index === 5 && 'scale-80',
      index === 6 && 'scale-[0.72]'
    );

  return (
    <div
      className={cn(
        'relative mx-auto my-4 w-full max-w-[480px] overflow-hidden',
        className
      )}
    >
      <div
        className={cn(
          'color-carousel-track relative flex h-20 select-none items-center justify-center py-2',
          trackClassName
        )}
        ref={carouselRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Color Items */}
        <div className="flex h-full items-center justify-center gap-2">
          {visibleColors.map((colorKey, index) => {
            const isCenter = index === 3;
            const swatchGradient = `linear-gradient(135deg, ${getColorStop(
              colorKey,
              '200'
            )} 0%, ${getColorStop(colorKey, '500')} 100%)`;
            const swatchContent = renderSwatch?.(colorKey);

            return (
              <div
                key={`${colorKey}-${index}`}
                className={getItemClassName(index, isCenter)}
                style={
                  renderSwatch ? undefined : { background: swatchGradient }
                }
                onClick={() => {
                  if (!isCenter) {
                    const diff = index - 3;
                    moveCarousel(diff);
                  }
                }}
                data-position={index}
                aria-selected={isCenter}
                role="option"
              >
                {swatchContent}
                {isCenter && (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 rounded-full border-[3px] border-neutral-50"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ColorCarousel;
